import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Video, Loader2, Save, CheckCircle2, AlertTriangle, ChevronDown, BookOpen, KeyRound, Clock, RefreshCw, Activity, ListMusic, Users } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import MarkdownLite from './MarkdownLite';
import setupGuide from '../../data/youtubeSetupGuide.json';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/** Collapsible setup panel with a down-caret header (all default-collapsed). */
function Section({ title, subtitle, icon: Icon, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="pt-3 border-t border-slate-200 dark:border-slate-800" data-testid={testid}>
      <CollapsibleTrigger className="flex w-full items-center justify-between text-left" data-testid={testid ? `${testid}-toggle` : undefined}>
        <span className="flex items-center gap-2">
          {Icon && <Icon className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" />}
          <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200">{title}</span>
          {subtitle && <span className="text-[10px] text-slate-500">· {subtitle}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-600 dark:text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pt-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

/** Small hover tooltip wrapper. Wrap the child in a span so it also shows on disabled buttons. */
function Tip({ text, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild><span className="inline-flex">{children}</span></TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-center leading-relaxed">{text}</TooltipContent>
    </Tooltip>
  );
}

/**
 * YouTubeScraperCard — Tech-Specialist-only Media box.
 * The public-only yt-dlp scraper is retired; all enumeration/sync now runs
 * through the YouTube Data API (OAuth) configurator below.
 */
export default function YouTubeScraperCard({ orgId }) {
  const { user } = useAuth();
  const isTech = !!user?.roles?.some(r =>
    ['tech_specialist', 'company_support', 'semprion_admin', 'admin'].includes(r.role_type) &&
    (['company_support', 'semprion_admin', 'admin'].includes(r.role_type) || r.org_id === orgId));

  const [channel, setChannel] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [legacyOpen, setLegacyOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/youtube-scrape/${orgId}/config`);
      setChannel(data.youtube_channel_url || '');
    } catch (_e) { /* 403 for non-tech — card is hidden anyway */ }
  }, [orgId]);

  useEffect(() => { if (isTech) load(); }, [isTech, load]);

  if (!isTech) return null;

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await axios.put(`${API}/api/youtube-scrape/${orgId}/config`, { youtube_channel_url: channel });
      setMsg({ ok: true, text: 'Channel saved' });
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || 'Save failed' });
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 p-4 space-y-3" data-testid="youtube-scraper-card">
      <div className="flex items-center gap-2">
        <Video className="h-4 w-4 text-red-500" />
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">YouTube Media Scraper</h3>
        <Badge variant="outline" className="bg-slate-100 dark:bg-slate-700/40 border-slate-300 dark:border-slate-600 text-[10px]">Tech only</Badge>
      </div>
      <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
        Links channel videos to games by date, team level and opponent, then stores each video URL on the
        matching game (camera icon on WP). Enumeration and sync run through the YouTube Data API (OAuth) below.
      </p>

      <Collapsible open={legacyOpen} onOpenChange={setLegacyOpen}>
        <CollapsibleTrigger className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" data-testid="yt-legacy-toggle">
          <ChevronDown className={`h-3 w-3 transition-transform ${legacyOpen ? 'rotate-180' : ''}`} />
          Legacy yt-dlp channel URL (no longer used)
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="flex items-center gap-2">
            <Input value={channel} onChange={e => setChannel(e.target.value)}
              placeholder="https://www.youtube.com/@your-channel"
              className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-8 text-xs" data-testid="youtube-channel-input" />
            <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={save} disabled={saving || !channel} data-testid="youtube-channel-save-btn">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {msg && (
        <div className={`flex items-center gap-1.5 text-[11px] ${msg.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-testid="youtube-scrape-msg">
          {msg.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
          {msg.text}
        </div>
      )}

      <YouTubeApiConfig orgId={orgId} />
    </div>
  );
}

/**
 * YouTubeApiConfig — YouTube Data API v3 (OAuth) configurator.
 * Setup panels are collapsed by default; day-to-day actions and a recent-activity
 * feed stay visible. A Setup guide button opens the full step-by-step doc.
 */
function YouTubeApiConfig({ orgId }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [audit, setAudit] = useState(null);
  const [resync, setResync] = useState(null);
  const [applyMedia, setApplyMedia] = useState(false);
  const [applyRes, setApplyRes] = useState(null);
  const [levelRows, setLevelRows] = useState([]);
  const [showGuide, setShowGuide] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/youtube-api/${orgId}/config`);
      setCfg(data);
    } catch (_e) { /* 403 hidden */ }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const lw = cfg?.level_words || {};
    setLevelRows(Object.entries(lw).map(([code, a]) => ({
      code, word: (a || ['', ''])[0] || '', gender: (a || ['', ''])[1] || '' })));
  }, [cfg]);

  if (!cfg) return null;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const pt = cfg.playlist_templates || {};
  const sw = cfg.sport_words || {};
  const aliases = cfg.opponent_aliases || [];
  const setTmpl = (k, v) => setCfg({ ...cfg, playlist_templates: { ...(cfg.playlist_templates || {}), [k]: v } });
  const setSportWord = (k, v) => setCfg({ ...cfg, sport_words: { ...(cfg.sport_words || {}), [k]: v } });
  const setLevelRow = (i, field, v) => setLevelRows(rows => rows.map((r, idx) => idx === i ? { ...r, [field]: v } : r));
  const addLevelRow = () => setLevelRows(rows => [...rows, { code: '', word: '', gender: '' }]);
  const removeLevelRow = (i) => setLevelRows(rows => rows.filter((_, idx) => idx !== i));
  const addAlias = () => setCfg({ ...cfg, opponent_aliases: [...(cfg.opponent_aliases || []), { canonical: '', aliases: [] }] });
  const setAlias = (i, field, v) => {
    const rows = [...(cfg.opponent_aliases || [])];
    rows[i] = { ...rows[i], [field]: field === 'aliases' ? v.split(',').map(s => s.trim()).filter(Boolean) : v };
    setCfg({ ...cfg, opponent_aliases: rows });
  };
  const removeAlias = (i) => {
    const rows = [...(cfg.opponent_aliases || [])]; rows.splice(i, 1);
    setCfg({ ...cfg, opponent_aliases: rows });
  };

  const saveCfg = async () => {
    setBusy(true); setNote(null);
    try {
      const payload = {
        client_id: cfg.client_id || '', sync_time: cfg.sync_time,
        expected_channel_id: cfg.expected_channel_id || '',
        sync_frequency: cfg.sync_frequency, target_visibility: cfg.target_visibility,
        auto_apply: !!cfg.auto_apply, read_only: !!cfg.read_only,
        season_rollover_month: Number(cfg.season_rollover_month) || 8,
        ingest_start_date: cfg.ingest_start_date || '',
        new_playlist_privacy: cfg.new_playlist_privacy,
        auto_create_playlist: !!cfg.auto_create_playlist,
        unlist_after_days: Math.min(14, Math.max(0, Number(cfg.unlist_after_days ?? 2))),
        sport_words: cfg.sport_words,
        level_words: Object.fromEntries(levelRows.filter(r => (r.code || '').trim()).map(r => [r.code.trim(), [r.word, r.gender]])),
        playlist_templates: cfg.playlist_templates, opponent_aliases: cfg.opponent_aliases,
      };
      if (cfg._client_secret) payload.client_secret = cfg._client_secret;
      const { data } = await axios.put(`${API}/api/youtube-api/${orgId}/config`, payload);
      setCfg(data); setNote({ ok: true, text: 'Saved' });
    } catch (e) { setNote({ ok: false, text: e.response?.data?.detail || 'Save failed' }); }
    finally { setBusy(false); }
  };

  const connect = async () => {
    setBusy(true); setNote(null);
    try {
      const { data } = await axios.post(`${API}/api/youtube-api/${orgId}/oauth/start`);
      window.open(data.auth_url, '_blank', 'noopener');
      setNote({ ok: true, text: 'Google consent opened in a new tab — approve there and you\u2019ll be connected automatically, then click Test connection to confirm.' });
    } catch (e) { setNote({ ok: false, text: e.response?.data?.detail || 'Connect failed' }); }
    finally { setBusy(false); }
  };

  const testConn = async () => {
    setBusy(true); setNote(null);
    try {
      const { data } = await axios.post(`${API}/api/youtube-api/${orgId}/test`);
      setNote(data.ok ? { ok: true, text: `Reached channel: ${data.title}` }
        : { ok: false, text: data.error || 'Not connected' });
    } catch (e) { setNote({ ok: false, text: e.response?.data?.detail || 'Test failed' }); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true); setNote(null);
    try {
      await axios.post(`${API}/api/youtube-api/${orgId}/sync-now`);
      setNote({ ok: true, text: 'Incremental sync started — see Recent activity below in a moment.' });
      setTimeout(load, 3000);
    } catch (e) { setNote({ ok: false, text: e.response?.data?.detail || 'Sync failed' }); }
    finally { setBusy(false); }
  };

  const runAudit = async () => {
    setBusy(true); setNote(null);
    try {
      const { data } = await axios.post(`${API}/api/youtube-api/${orgId}/library-audit`);
      if (data.ok) {
        setAudit(data);
        setNote({ ok: true, text: `Library: ${data.total_videos} videos (see breakdown).` });
        setTimeout(load, 800);
      } else { setNote({ ok: false, text: data.error || 'Audit failed' }); }
    } catch (e) { setNote({ ok: false, text: e.response?.data?.detail || 'Audit failed' }); }
    finally { setBusy(false); }
  };

  const runResync = async (dryRun) => {
    setBusy(true); setNote(null);
    try {
      const { data } = await axios.post(`${API}/api/youtube-api/${orgId}/full-resync`,
        { dry_run: dryRun, apply_media: dryRun ? false : applyMedia });
      setResync({ ...data, dry_run: dryRun });
      const ok = (data.errors || []).length === 0;
      setNote({ ok, text: dryRun
        ? `Preview: ${data.new_videos} new · ${data.already_in_master} already in master`
        : `Applied: ${data.new_videos} ingested${data.media_writes ? ` · ${data.media_writes} media links` : ''}` });
      setTimeout(load, dryRun ? 800 : 1500);
    } catch (e) { setNote({ ok: false, text: e.response?.data?.detail || 'Resync failed' }); }
    finally { setBusy(false); }
  };

  const inputCls = 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-8 text-xs';
  const runLog = (cfg.run_log || []).slice().reverse();
  const writesOn = !!cfg.auto_apply && !cfg.read_only;
  const setWritesOn = (val) => setCfg({ ...cfg, auto_apply: val, read_only: !val });

  const scheduleOn = (cfg.sync_frequency || 'daily') !== 'off';
  const toggleSchedule = async () => {
    const next = scheduleOn ? 'off' : 'daily';
    setBusy(true); setNote(null);
    try {
      await axios.put(`${API}/api/youtube-api/${orgId}/config`, { sync_frequency: next });
      setNote({ ok: true, text: next === 'off'
        ? 'Scheduled automatic scraping is now OFF.'
        : `Scheduled automatic scraping is now ON — runs daily at ${cfg.sync_time || '03:30'} (${cfg.timezone_effective || 'local'}).` });
      await load();
    } catch (e) { setNote({ ok: false, text: e.response?.data?.detail || 'Save failed' }); }
    finally { setBusy(false); }
  };

  const applyWrites = async (dryRun) => {
    if (!dryRun && !window.confirm(
      `This will change your YouTube channel: set matched videos to "${cfg.target_visibility || 'unlisted'}" and add them to their season/level playlist (up to 10 per run). Continue?`)) return;
    setBusy(true); setNote(null);
    try {
      const { data } = await axios.post(`${API}/api/youtube-api/${orgId}/apply-writes`,
        { dry_run: dryRun, limit: 10 });
      if (data.ok === false) { setNote({ ok: false, text: data.error || 'Apply failed' }); }
      else {
        setApplyRes({ ...data });
        setNote({ ok: true, text: dryRun
          ? `${data.candidates} matched video(s) still need writes — showing up to ${data.results?.length || 0}`
          : `Applied to ${data.changed} video(s)${data.skipped_pending ? ` · ${data.skipped_pending} live skipped` : ''}${data.candidates - data.changed > 0 ? ` · ${Math.max(0, data.candidates - data.changed)} remaining (re-run)` : ''}` });
      }
      setTimeout(load, 1200);
    } catch (e) { setNote({ ok: false, text: e.response?.data?.detail || 'Apply failed' }); }
    finally { setBusy(false); }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-3" data-testid="youtube-api-config">
        {/* header + setup guide */}
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">YouTube Data API (OAuth)</h4>
          <Badge variant="outline" className={`text-[10px] ${cfg.connected ? 'bg-emerald-50 dark:bg-emerald-700/30 border-emerald-600 text-emerald-700 dark:text-emerald-300' : 'bg-slate-100 dark:bg-slate-700/40 border-slate-300 dark:border-slate-600'}`}>
            {cfg.connected ? `Connected: ${cfg.channel_title || 'channel'}` : 'Not connected'}
          </Badge>
          {writesOn
            ? <Badge variant="outline" className="text-[10px] bg-amber-50 dark:bg-amber-700/30 border-amber-600 text-amber-700 dark:text-amber-300">Auto-writes ON</Badge>
            : <Badge variant="outline" className="text-[10px] bg-slate-100 dark:bg-slate-700/40 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300">Read-only (safe)</Badge>}
          <Button size="sm" variant="outline" className="ml-auto h-7 text-[11px] border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
            onClick={() => setShowGuide(true)} data-testid="yt-setup-guide-btn">
            <BookOpen className="h-3 w-3 mr-1" /> Setup guide
          </Button>
        </div>

        {/* day-to-day actions (always visible, with tooltips) */}
        <div className="flex items-center gap-2 flex-wrap">
          <Tip text="Checks that OmniLeagues can reach your connected channel right now, and shows its name.">
            <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={testConn} disabled={busy} data-testid="yt-test-btn">Test connection</Button>
          </Tip>
          <Tip text="Looks for brand-new uploads since the last run and links them to their games. Honours Read-only (won't change your channel). Results appear under Recent activity.">
            <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={syncNow} disabled={busy || !cfg.connected} data-testid="yt-sync-now-btn">Sync now</Button>
          </Tip>
          <Tip text="Read-only count of your ENTIRE library by visibility (public / unlisted / private). Never changes anything — use it to confirm the connection sees all videos.">
            <Button size="sm" variant="outline" className="h-8 text-xs border-sky-700 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-900/30"
              onClick={runAudit} disabled={busy || !cfg.connected} data-testid="yt-audit-btn">Library audit</Button>
          </Tip>
        </div>

        {audit && (
          <div data-testid="yt-audit-result" className="text-[11px] text-slate-700 dark:text-slate-300 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-2">
            <span className="font-bold text-slate-800 dark:text-slate-200">Library audit (read-only): </span>
            total <b>{audit.total_videos}</b> · public {audit.by_visibility?.public || 0} ·
            unlisted {audit.by_visibility?.unlisted || 0} · private {audit.by_visibility?.private || 0}
          </div>
        )}

        {/* ── Setup panels (collapsed by default) ── */}
        <Section title="Connection & credentials" subtitle="rarely changes once set up" icon={KeyRound} testid="yt-sec-connection">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-slate-700 dark:text-slate-300 text-[11px]">OAuth Client ID</label>
              <Input value={cfg.client_id || ''} onChange={e => set('client_id', e.target.value)} className={inputCls} data-testid="yt-client-id" />
            </div>
            <div className="space-y-1">
              <label className="text-slate-700 dark:text-slate-300 text-[11px]">OAuth Client Secret {cfg.client_secret_set && '(saved)'}</label>
              <Input type="password" placeholder={cfg.client_secret_set ? 'leave blank to keep' : ''}
                onChange={e => set('_client_secret', e.target.value)} className={inputCls} data-testid="yt-client-secret" />
            </div>
            <div className="space-y-1 col-span-2">
              <label className="text-slate-700 dark:text-slate-300 text-[11px]">Expected channel ID (brand-account guard — refuses a wrong chooser pick)</label>
              <Input value={cfg.expected_channel_id || ''} onChange={e => set('expected_channel_id', e.target.value)}
                placeholder="UC..." className={inputCls} data-testid="yt-expected-channel" />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={saveCfg} disabled={busy} data-testid="yt-save-btn">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
            </Button>
            <Tip text="One-time setup: opens Google's consent screen so OmniLeagues can manage this channel. Already connected — you won't normally need this again.">
              <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40"
                onClick={connect} disabled={busy} data-testid="yt-connect-btn">Connect YouTube</Button>
            </Tip>
          </div>
        </Section>

        <Section title="Schedule & write policy" subtitle="daily sync time + safety switches" icon={Clock} testid="yt-sec-schedule" defaultOpen>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-slate-700 dark:text-slate-300 text-[11px]">Daily sync time ({cfg.timezone_effective || 'local'})</label>
              <Input type="time" value={cfg.sync_time || '03:30'} onChange={e => set('sync_time', e.target.value)}
                className={`${inputCls} ol-dark-picker`} data-testid="yt-sync-time" />
            </div>
            <div className="space-y-1">
              <label className="text-slate-700 dark:text-slate-300 text-[11px]">Scheduled automatic scraping</label>
              <button type="button" onClick={toggleSchedule} disabled={busy || !cfg.connected}
                data-testid="yt-schedule-enabled-toggle"
                className={`flex w-full items-center justify-between h-8 px-3 rounded text-xs font-semibold border transition-colors disabled:opacity-50 ${scheduleOn ? 'bg-emerald-50 dark:bg-emerald-700/30 border-emerald-600 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-700/40' : 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}>
                <span className="flex items-center gap-2">
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${scheduleOn ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                  {scheduleOn ? 'ON — runs daily' : 'OFF — disabled'}
                </span>
                <span className="text-[10px] font-normal opacity-70">{scheduleOn ? 'click to disable' : 'click to enable'}</span>
              </button>
            </div>
            <div className="space-y-1">
              <label className="text-slate-700 dark:text-slate-300 text-[11px]">New-video visibility</label>
              <select value={cfg.target_visibility || 'unlisted'} onChange={e => set('target_visibility', e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-8 text-xs rounded px-2" data-testid="yt-visibility">
                <option value="unlisted">Unlisted</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="leave">Leave unchanged</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-slate-700 dark:text-slate-300 text-[11px]">Season rollover month</label>
              <Input type="number" min="1" max="12" value={cfg.season_rollover_month || 8}
                onChange={e => set('season_rollover_month', e.target.value)} className={inputCls} data-testid="yt-rollover" />
            </div>
            <div className="space-y-1">
              <label className="text-slate-700 dark:text-slate-300 text-[11px]">Ingest start date (skip videos before)</label>
              <Input type="date" value={cfg.ingest_start_date || ''} onChange={e => set('ingest_start_date', e.target.value)}
                className={`${inputCls} ol-dark-picker`} data-testid="yt-ingest-start-date" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            The daily job runs once a day at the time above, interpreted in your organization&apos;s
            time zone{cfg.timezone_effective ? ` (${cfg.timezone_effective})` : ''}. Use the
            <b> Scheduled automatic scraping</b> ON/OFF button to turn the nightly run on or off (saves immediately).
            {' '}<b>Ingest start date</b> skips any video dated before it (undated videos too) and <b>auto-advances</b> to the
            newest game date it processes — set an earlier date to re-scan farther back. Game dates come from YouTube&apos;s live
            start time (converted to your local zone) when available, else the title date.
          </p>
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-2.5 space-y-2">
            <label className="flex items-start gap-2 text-[11px] text-slate-800 dark:text-slate-200 cursor-pointer">
              <input type="checkbox" checked={writesOn} className="mt-0.5"
                onChange={e => setWritesOn(e.target.checked)} data-testid="yt-writes-enabled" />
              <span>
                <b>Auto-set Unlisted + playlist on match</b>
                <span className="block text-[10px] text-slate-500 leading-relaxed">
                  When ON, each newly-matched video is set to your chosen visibility and added to its
                  season/level playlist automatically during the nightly sync. When OFF (recommended
                  default) the scraper still links video URLs to games but never changes your channel.
                </span>
              </span>
            </label>
            <div className={`flex items-start gap-2 pl-5 ${writesOn ? '' : 'opacity-50'}`}
                 data-testid="yt-unlist-delay-block">
              <input
                type="number" min={0} max={14} step={1}
                disabled={!writesOn}
                value={cfg.unlist_after_days ?? 2}
                onChange={e => {
                  const v = e.target.value;
                  set('unlist_after_days', v === '' ? '' : Math.min(14, Math.max(0, Number(v))));
                }}
                onBlur={e => { if (e.target.value === '') set('unlist_after_days', 2); }}
                className="w-16 h-7 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-[11px] text-slate-900 dark:text-slate-100"
                data-testid="yt-unlist-after-days"
              />
              <span className="text-[11px] text-slate-800 dark:text-slate-200">
                <b>Days public before unlisting</b>
                <span className="block text-[10px] text-slate-500 leading-relaxed">
                  How many days a matched video stays <b>public</b> after it goes up, before OL sets
                  it to your chosen visibility. 0&ndash;14; default 2. The video link is added to the
                  schedule straight away either way — it is visible to everyone while the video is
                  public, and members-only once OL unlists it. <b>0</b> unlists immediately on match.
                </span>
                <span className="block text-[10px] mt-1 text-slate-600 dark:text-slate-400">
                  {cfg.unlist_effective_from
                    ? <>Unlisting applies to videos dated <b>{cfg.unlist_effective_from}</b> or later.
                        Anything older is left alone — use <b>Apply write policy now</b> to reach
                        further back deliberately.</>
                    : <>Not started. Unlisting begins the day you switch the box above on, and only
                        applies from that day forward — turning it on never hides the back catalogue.</>}
                </span>
              </span>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-700 dark:text-slate-300">
              <input type="checkbox" checked={!!cfg.auto_create_playlist}
                onChange={e => set('auto_create_playlist', e.target.checked)} data-testid="yt-auto-create" />
              Auto-create the playlist if it doesn&apos;t exist yet
            </label>
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
            onClick={saveCfg} disabled={busy} data-testid="yt-save-schedule-btn">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
          </Button>

          <div className="pt-2 mt-1 border-t border-slate-200 dark:border-slate-800/60 space-y-1.5" data-testid="yt-apply-writes-block">
            <p className="text-[11px] font-bold text-slate-800 dark:text-slate-200">Apply write policy now</p>
            <p className="text-[10px] text-slate-500 leading-relaxed">
              One-off: sets <b>already-matched</b> videos that are still public to your chosen visibility
              and adds them to their playlist (skips any live/unfinished stream). Use this to apply writes
              to videos that were linked while writes were off. Processes up to 10 per run — re-run to continue.
            </p>
            <div className="flex items-center gap-2">
              <Tip text="Read-only preview — lists matched videos that still need a visibility/playlist change. Changes nothing.">
                <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => applyWrites(true)} disabled={busy || !cfg.connected} data-testid="yt-apply-preview-btn">Preview</Button>
              </Tip>
              <Tip text="Applies the write policy for real to the matched, non-live videos shown in the preview (up to 10).">
                <Button size="sm" className="bg-amber-700 hover:bg-amber-800 h-8 text-xs"
                  onClick={() => applyWrites(false)} disabled={busy || !cfg.connected} data-testid="yt-apply-run-btn">Apply now</Button>
              </Tip>
            </div>
            {applyRes && (
              <div data-testid="yt-apply-result" className="text-[11px] text-slate-700 dark:text-slate-300">
                {applyRes.dry_run ? 'Preview' : 'Applied'}: <b>{applyRes.candidates}</b> matched
                video(s) still need writes{typeof applyRes.changed === 'number' && !applyRes.dry_run ? ` · ${applyRes.changed} updated` : ''}
                {applyRes.skipped_pending ? ` · ${applyRes.skipped_pending} live skipped` : ''}
                {applyRes.results?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-2 mt-1 space-y-0.5">
                    {applyRes.results.map((r, i) => (
                      <div key={i} className="text-[10px] flex items-center gap-2" data-testid={`yt-apply-row-${i}`}>
                        <span className="text-slate-700 dark:text-slate-300 truncate flex-1">{r.title || r.video_id}</span>
                        <span className="text-emerald-700 dark:text-emerald-300/80 whitespace-nowrap">
                          {r.status || (r.would ? r.would.join(', ') : (r.actions ? r.actions.join(', ') : '')) || (r.error ? `err: ${r.error}` : '—')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>

        <Section title="Full resync (safe, one-off)" subtitle="ingest videos missing from the master" icon={RefreshCw} testid="yt-sec-resync">
          <p className="text-[10px] text-slate-500">
            Enumerates the WHOLE library via OAuth and ingests only NEW videos into the master list.
            Existing rows are never modified; game media is only written into empty slots. Preview first.
          </p>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={applyMedia} onChange={e => setApplyMedia(e.target.checked)} data-testid="yt-resync-apply-media" />
            Also fill empty game media slots (+ WP push) on Apply
          </label>
          <div className="flex items-center gap-2">
            <Tip text="Dry run — shows how many videos WOULD be added and how many are already known. Writes nothing.">
              <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                onClick={() => runResync(true)} disabled={busy || !cfg.connected} data-testid="yt-resync-preview-btn">Preview</Button>
            </Tip>
            <Tip text="Runs the resync for real: adds any new videos to the master list (existing rows are never changed). If the box above is ticked, it also links videos into empty game media slots and queues a WordPress update.">
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 h-8 text-xs"
                onClick={() => runResync(false)} disabled={busy || !cfg.connected} data-testid="yt-resync-apply-btn">Apply resync</Button>
            </Tip>
          </div>
          {resync && (
            <div data-testid="yt-resync-result" className="text-[11px] text-slate-700 dark:text-slate-300">
              {resync.dry_run ? 'Preview' : 'Applied'}: {resync.new_videos} new ·
              {' '}{resync.already_in_master} already in master · {resync.new_matched} matched
              {!resync.dry_run && typeof resync.media_writes === 'number' ? ` · ${resync.media_writes} media links` : ''}
            </div>
          )}
          {resync?.new_details?.length > 0 && (
            <div className="max-h-44 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-2 space-y-0.5" data-testid="yt-resync-details">
              {resync.new_details.map((d, i) => (
                <div key={i} className="text-[10px] flex items-center gap-2" data-testid={`yt-resync-detail-${i}`}>
                  <span className={d.match_status === 'MATCHED' ? 'text-emerald-600 dark:text-emerald-400' : (d.match_status === 'PENDING' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500')}>{d.match_status}</span>
                  <span className="text-slate-500 whitespace-nowrap">{d.game_date || 'no date'}</span>
                  <span className="text-slate-600 dark:text-slate-400 whitespace-nowrap">{d.level_code || '—'}</span>
                  <span className="text-slate-700 dark:text-slate-300 truncate flex-1">{d.title}</span>
                  {d.matched_game_title && <span className="text-emerald-700 dark:text-emerald-300/70 truncate max-w-[40%]">→ {d.matched_game_title}</span>}
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Playlist naming & levels" subtitle="how playlists are named per sport/level" icon={ListMusic} testid="yt-sec-naming">
          <p className="text-[10px] text-slate-500">Tokens: {'{sport} {level} {gender} {dev} {season} {code}'} — per-sport key or default.</p>
          {['bk', 'bb', 'vl', 'default'].map(k => (
            <div key={k} className="flex items-center gap-2">
              <span className="text-[11px] text-slate-600 dark:text-slate-400 w-14">{k}</span>
              <Input value={pt[k] ?? (k === 'bk' ? '{sport} {level} {gender}{dev} {season} ({code})' : '{sport} {level} {season}')}
                onChange={e => setTmpl(k, e.target.value)} className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-7 text-[11px]" data-testid={`yt-tmpl-${k}`} />
            </div>
          ))}
          <div className="grid grid-cols-3 gap-2 pt-1">
            {['bk', 'bb', 'vl'].map(k => (
              <div key={k} className="flex items-center gap-1">
                <span className="text-[10px] text-slate-600 dark:text-slate-400 w-5">{k}</span>
                <Input value={sw[k] || ''} onChange={e => setSportWord(k, e.target.value)}
                  className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-7 text-[11px]" data-testid={`yt-sport-${k}`} />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] text-slate-600 dark:text-slate-400">Levels → code, level word, gender</p>
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={addLevelRow} data-testid="yt-level-add">+ Add level</Button>
          </div>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            To add a new team level (e.g. a <b>JV Boys White</b> squad), click <b>+ Add level</b>, give it a short code like
            <code className="mx-1 px-1 rounded bg-slate-100 dark:bg-slate-800 text-emerald-700 dark:text-emerald-300">jvbw</code>, level word <b>JV White</b>, gender <b>Boys</b>, then Save.
            This controls playlist <i>naming</i>; video-to-game matching still keys off the game's own level.
          </p>
          {levelRows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={r.code} onChange={e => setLevelRow(i, 'code', e.target.value)} placeholder="code"
                className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-7 text-[11px] w-16" data-testid={`yt-lvl-code-${i}`} />
              <Input value={r.word} onChange={e => setLevelRow(i, 'word', e.target.value)} placeholder="level word"
                className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-7 text-[11px] flex-1" data-testid={`yt-lvl-word-${i}`} />
              <Input value={r.gender} onChange={e => setLevelRow(i, 'gender', e.target.value)} placeholder="gender"
                className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-7 text-[11px] w-24" data-testid={`yt-lvl-gender-${i}`} />
              <button onClick={() => removeLevelRow(i)} className="text-red-600 dark:text-red-400 text-[11px] px-1 hover:text-red-700 dark:hover:text-red-300" data-testid={`yt-lvl-del-${i}`}>Remove</button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
            onClick={saveCfg} disabled={busy} data-testid="yt-save-naming-btn">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
          </Button>
        </Section>

        <Section title="Opponent aliases" subtitle="lift the match rate" icon={Users} testid="yt-sec-aliases">
          <div className="flex items-center justify-end">
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={addAlias} data-testid="yt-alias-add">+ Row</Button>
          </div>
          {aliases.map((row, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={row.canonical || ''} onChange={e => setAlias(i, 'canonical', e.target.value)}
                placeholder="canonical" className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-7 text-[11px] w-40" data-testid={`yt-alias-canon-${i}`} />
              <Input value={(row.aliases || []).join(', ')} onChange={e => setAlias(i, 'aliases', e.target.value)}
                placeholder="alias1, alias2, ..." className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 h-7 text-[11px] flex-1" data-testid={`yt-alias-vals-${i}`} />
              <button onClick={() => removeAlias(i)} className="text-red-600 dark:text-red-400 text-[11px] px-1 hover:text-red-700 dark:hover:text-red-300" data-testid={`yt-alias-del-${i}`}>Remove</button>
            </div>
          ))}
          {aliases.length === 0 && <p className="text-[10px] text-slate-500">No aliases yet — add rows, then Save.</p>}
          <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
            onClick={saveCfg} disabled={busy} data-testid="yt-save-aliases-btn">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Save
          </Button>
        </Section>

        {/* Recent activity (always visible) */}
        <div className="pt-3 border-t border-slate-200 dark:border-slate-800 space-y-1" data-testid="yt-activity">
          <div className="flex items-center gap-2">
            <Activity className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" />
            <h5 className="text-[11px] font-bold text-slate-800 dark:text-slate-200">Recent activity</h5>
            {cfg.running && <Badge variant="outline" className="text-[10px] bg-sky-50 dark:bg-sky-700/30 border-sky-600 text-sky-700 dark:text-sky-300">running…</Badge>}
          </div>
          <p className="text-[10px] text-slate-500">
            {cfg.last_successful_run
              ? `Last successful sync: ${new Date(cfg.last_successful_run).toLocaleString()}`
              : 'No successful sync yet.'}
            {cfg.last_error && <span className="text-red-600 dark:text-red-400"> · last error: {cfg.last_error}</span>}
          </p>
          {runLog.length === 0 && <p className="text-[10px] text-slate-600">No runs recorded yet — click Sync now, Library audit, or Preview.</p>}
          {runLog.map((e, i) => (
            <div key={i} className="flex items-start gap-2 text-[10px]" data-testid={`yt-activity-row-${i}`}>
              <span className={e.ok === false ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}>{e.ok === false ? '\u2715' : '\u2713'}</span>
              <span className="text-slate-500 whitespace-nowrap">{new Date(e.at).toLocaleString()}</span>
              <span className="text-slate-700 dark:text-slate-300 font-medium">{e.action}</span>
              <span className="text-slate-600 dark:text-slate-400">— {e.summary}{e.error ? `: ${e.error}` : ''}</span>
            </div>
          ))}
        </div>

        {note && (
          <div className={`flex items-center gap-1.5 text-[11px] ${note.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-testid="yt-api-note">
            {note.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}{note.text}
          </div>
        )}

        {/* Setup guide modal */}
        <Dialog open={showGuide} onOpenChange={setShowGuide}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200" data-testid="yt-setup-guide-modal">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-slate-100">Connecting a YouTube Channel</DialogTitle>
            </DialogHeader>
            <div className="text-[12px] leading-relaxed text-slate-700 dark:text-slate-300 space-y-2
              [&_h1]:text-base [&_h1]:font-bold [&_h1]:text-slate-100 [&_h1]:mt-2
              [&_h2]:text-sm [&_h2]:font-bold [&_h2]:text-slate-100 [&_h2]:mt-3
              [&_h3]:text-[12px] [&_h3]:font-semibold [&_h3]:text-slate-200 [&_h3]:mt-2
              [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5
              [&_code]:bg-slate-800 [&_code]:px-1 [&_code]:rounded [&_code]:text-emerald-300 [&_code]:text-[11px] [&_code]:break-all
              [&_pre]:bg-slate-800 [&_pre]:p-2 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:text-[11px]
              [&_a]:text-sky-400 [&_a]:underline [&_a]:break-all
              [&_table]:w-full [&_th]:text-left [&_th]:border-b [&_th]:border-slate-700 [&_th]:py-1 [&_th]:pr-3 [&_th]:text-[11px]
              [&_td]:border-b [&_td]:border-slate-800 [&_td]:py-1 [&_td]:pr-3 [&_td]:text-[11px] [&_td]:align-top
              [&_blockquote]:border-l-2 [&_blockquote]:border-slate-600 [&_blockquote]:pl-3 [&_blockquote]:text-slate-400 [&_blockquote]:italic
              [&_hr]:border-slate-700 [&_hr]:my-3 [&_strong]:text-slate-100">
            <MarkdownLite text={setupGuide.content} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
